import { STATUS } from "@/constants/statuses";
import {
    DownloadImageOptions,
    REWARDS_QR_CANVAS_HEIGHT,
    REWARDS_QR_CODE_TYPES,
    REWARDS_QR_DOWNLOAD_IMAGE_HEIGHT,
    REWARDS_QR_DOWNLOAD_IMAGE_WIDTH,
    REWARDS_QR_SHOP_LOGO_HEIGHT,
    REWARDS_QR_SHOP_LOGO_WIDTH,
} from "@apps/rewards/modules/QRCode/types";
import RewardsStorefrontValuesService from "@apps/rewards/services/RewardsStorefrontValuesService";
import { rewardsApplicationLoggerToken } from "@apps/rewards/tokens";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import CanvasManager from "@modules/canvas_module/CanvasManager/CanvasManager";
import {
    ImageOptions,
    TextOptions,
} from "@modules/canvas_module/CanvasManager/types";
import { CanvasModule } from "@modules/canvas_module/CanvasModule";
import {
    Observable,
    catchError,
    combineLatest,
    forkJoin,
    map,
    of,
    switchMap,
    tap,
} from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import RewardsQRCodeApiService from "./RewardsQRCodeApiService";
import type { RewardsQRCodeEntity } from "./RewardsQRCodeEntity";
import RewardsQRCodeQuery from "./RewardsQRCodeQuery";
import RewardsQRCodeStore from "./RewardsQRCodeStore";

@singleton()
class RewardsQRCodeService {
    private readonly qrCodeType$: Observable<REWARDS_QR_CODE_TYPES>;
    private readonly qrCode$: Observable<string>;
    private readonly canvasManager: CanvasManager;

    constructor(
        private readonly rewardsQRCodeApiService: RewardsQRCodeApiService,
        private readonly rewardsQRCodeStore: RewardsQRCodeStore,
        private readonly rewardsQrCodeQuery: RewardsQRCodeQuery,
        private readonly rewardsStorefrontValuesService: RewardsStorefrontValuesService,
        private readonly canvasModule: CanvasModule,
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {
        this.canvasManager = this.canvasModule.exports.canvasManager;

        this.qrCodeType$ = this.rewardsStorefrontValuesService
            .selectRewardsPosSettings()
            .pipe(map((settings) => settings.codeType));

        this.qrCode$ = this.rewardsQrCodeQuery.qrCode$;
    }

    getQRCode(): Observable<RewardsQRCodeEntity> {
        this.logger.debug("RewardsQRCodeService.getQRCode is started");

        const status = this.rewardsQRCodeStore.getStatus();
        this.rewardsQRCodeStore.setStatus(getLoadingStatus(status));

        return this.rewardsQRCodeApiService.getQRCode().pipe(
            catchError((error: unknown) => {
                this.logger.debug(
                    "RewardsQRCodeService.getQRCode is errored",
                    error
                );
                this.rewardsQRCodeStore.setStatus(STATUS.ERROR);
                throw error;
            }),
            tap((value) => {
                this.logger.debug("RewardsQRCodeService.getQRCode is ready");

                applyTransaction(() => {
                    this.rewardsQRCodeStore.update((state) => ({
                        ...state,
                        qrCode: value,
                    }));
                    this.rewardsQRCodeStore.setStatus(STATUS.READY);
                });
            })
        );
    }

    selectDownloadImageDataUrl({
        logo,
        description,
        shopDomain,
    }: DownloadImageOptions): Observable<string> {
        return combineLatest([this.qrCode$, this.qrCodeType$]).pipe(
            switchMap(([qrCode, qrCodeType]) => {
                const descriptionWidth =
                    this.canvasManager.getMeasureTextWidth(description) + 20;

                const logoHeight = logo ? REWARDS_QR_SHOP_LOGO_HEIGHT : 0;

                const canvasHeightPadding = logo ? 15 : 30;

                const canvasHeight =
                    REWARDS_QR_CANVAS_HEIGHT + logoHeight + canvasHeightPadding;


                if (description) {
                    this.canvasManager.setCanvasSize(
                        descriptionWidth,
                        canvasHeight
                    );
                }

                const logoOptions: ImageOptions = {
                    positionX:
                        (descriptionWidth - REWARDS_QR_SHOP_LOGO_WIDTH) / 2,
                    positionY: 20,
                    height: logoHeight,
                    width: REWARDS_QR_SHOP_LOGO_WIDTH,
                };

                const shopDomainOptions: TextOptions = {
                    positionX: descriptionWidth / 2,
                    positionY: logoHeight + 40,
                    textColor: "#1773B0",
                    textAlign: "center",
                };

                const qrCodeOptions: ImageOptions = {
                    positionX:
                        (descriptionWidth -
                            REWARDS_QR_DOWNLOAD_IMAGE_WIDTH[qrCodeType]) /
                        2,
                    positionY: logoHeight + 50,
                    width: REWARDS_QR_DOWNLOAD_IMAGE_WIDTH[qrCodeType],
                    height: REWARDS_QR_DOWNLOAD_IMAGE_HEIGHT[qrCodeType],
                };

                const descriptionOptions: TextOptions = {
                    positionX: descriptionWidth / 2,
                    positionY:
                        logoHeight +
                        REWARDS_QR_DOWNLOAD_IMAGE_HEIGHT[qrCodeType] +
                        70,
                    textAlign: "center",
                };

                return forkJoin([
                    this.canvasManager.drawImage(qrCode, qrCodeOptions),
                    logo
                        ? this.canvasManager.drawImage(logo, logoOptions)
                        : of(null),
                    this.canvasManager.drawText(shopDomain, shopDomainOptions),
                    this.canvasManager.drawText(
                        description,
                        descriptionOptions
                    ),
                ]);
            }),
            map(() => this.canvasManager.selectDrawingImageDataUrl()),
            tap(() => {
                this.canvasManager.clearCanvas();
            })
        );
    }
}

export default RewardsQRCodeService;
